import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Store, Video, FileText, Package, Building2, Image, Play, Crown, Star, MapPin, Globe, Phone, Mail, ArrowLeft, Lock, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
// AI Chat disabled - will reconnect when more shows/exhibitors available
// import AIChat from '../components/AIChat';
import AutoTranslatedText from '../components/AutoTranslatedText';
import OptimizedImage from '../components/OptimizedImage';
import SEOHead from '../components/SEOHead';
import { generateBoothSchema, generateVideoSchema, generateBreadcrumbSchema, SEO_CONFIG } from '../utils/seo';
import { parseLocalDate } from '../utils/dateUtils';

const BoothPage = () => {
  const { boothId } = useParams();
  const { user } = useContext(AuthContext);
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const currentLang = i18n.language || 'en';
  const [booth, setBooth] = useState(null);
  const [products, setProducts] = useState([]);
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [hasAccess, setHasAccess] = useState(false);
  const [accessChecked, setAccessChecked] = useState(false);
  
  // Interaction modal state
  const [showInteractionModal, setShowInteractionModal] = useState(false);
  const [interactionType, setInteractionType] = useState('call_request');
  const [interactionMessage, setInteractionMessage] = useState('');

  useEffect(() => {
    fetchBoothData();
  }, [boothId]);

  // Check access when user changes
  useEffect(() => {
    if (booth && user) {
      checkAccess();
    } else if (booth && !user) {
      setHasAccess(false);
      setAccessChecked(true);
    }
  }, [booth, user]);

  const checkAccess = async () => {
    if (!user) {
      setHasAccess(false);
      setAccessChecked(true);
      return;
    }
    try {
      const response = await axios.get(`${API}/booths/${boothId}/access`);
      setHasAccess(response.data.has_access);
    } catch (error) {
      console.error('Failed to check access', error);
      setHasAccess(false);
    } finally {
      setAccessChecked(true);
    }
  };

  const fetchBoothData = async () => {
    try {
      const [boothRes, productsRes] = await Promise.all([
        axios.get(`${API}/booths/${boothId}`),
        axios.get(`${API}/products?booth_id=${boothId}`),
      ]);
      setBooth(boothRes.data);
      setProducts(productsRes.data);
      
      // Fetch event details
      if (boothRes.data.event_id) {
        const eventRes = await axios.get(`${API}/events/${boothRes.data.event_id}`);
        setEvent(eventRes.data);
      }
    } catch (error) {
      console.error('Failed to fetch booth', error);
      toast.error('Failed to load booth details');
    } finally {
      setLoading(false);
    }
  };

  const getTierBadge = (tier) => {
    if (tier === 'elite') {
      return (
        <Badge className="bg-purple-100 text-purple-700 border-2 border-purple-400 ring-2 ring-purple-200">
          <Crown className="h-3 w-3 mr-1 fill-purple-500" />
          {t('event_detail.tier_elite')}
        </Badge>
      );
    }
    if (tier === 'premium') {
      return (
        <Badge className="bg-orange-100 text-orange-700 border border-orange-200">
          <Star className="h-3 w-3 mr-1 fill-orange-500" />
          {t('event_detail.tier_premium')}
        </Badge>
      );
    }
    return (
      <Badge className="bg-slate-100 text-slate-700 border border-slate-200">
        {t('event_detail.tier_basic')}
      </Badge>
    );
  };

  const getImageUrl = (url) => {
    if (!url) return null;
    return url.startsWith('/api') ? `${API}${url.replace('/api', '')}` : url;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>{t('booth.loading_booth')}</p>
      </div>
    );
  }

  if (!booth) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>{t('booth.booth_not_found')}</p>
      </div>
    );
  }

  // Block buyer/guest access to booth details when event is not live
  // Sellers can still see their own booths, admins can see everything
  const isOwner = user && (user.role === 'admin' || user.role === 'seller');
  const eventIsLive = event?.status === 'live';
  
  if (!eventIsLive && !isOwner) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="booth-locked-page">
        <div className="max-w-md text-center p-8">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-amber-50 mb-6">
            <Lock className="h-10 w-10 text-amber-500" />
          </div>
          <h2 className="text-2xl font-bold text-slate-800 mb-3">{t('event_detail.booth_details_locked')}</h2>
          <p className="text-slate-500 mb-2">
            <strong>{booth.company_name}</strong> — {t('event_detail.booth_details_locked_desc')}
          </p>
          {event && (
            <p className="text-sm text-slate-400 mb-6">
              Event: {event.name} — Status: {event.status === 'registration_open' ? 'Registration Open' : event.status?.replace('_', ' ')}
            </p>
          )}
          <Button onClick={() => navigate(-1)} variant="outline">
            <ArrowLeft className="h-4 w-4 mr-2" /> {t('event_detail.go_back')}
          </Button>
        </div>
      </div>
    );
  }

  const galleryImages = (booth.media || []).filter(m => m.type === 'image');
  const videos = (booth.media || []).filter(m => m.type === 'video');
  const pdfs = (booth.media || []).filter(m => m.type === 'pdf');

  // Generate structured data for booth (includes VideoObject schemas if videos exist)
  const generateBoothStructuredData = () => {
    const schemas = [generateBoothSchema(booth)];
    
    // Add VideoObject schema for each video (fixes "Video isn't on a watch page" issue)
    videos.forEach((video, index) => {
      schemas.push(generateVideoSchema({
        name: video.name || `${booth.company_name} Video ${index + 1}`,
        description: `Product video from ${booth.company_name} exhibitor booth on TradeHub24`,
        url: video.url,
        thumbnailUrl: booth.banner_url || booth.logo_url,
        uploadDate: video.created_at || new Date().toISOString()
      }));
    });
    
    // Add breadcrumb schema for better search results navigation
    schemas.push(generateBreadcrumbSchema([
      { name: 'Home', url: '/' },
      { name: 'Exhibitors', url: '/exhibitors' },
      { name: booth.company_name }
    ]));
    
    return schemas;
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="booth-page">
      {/* SEO with Booth, Video, and Breadcrumb Schema */}
      <SEOHead 
        title={`${booth.company_name} | TradeHub24 Virtual Booth`}
        description={booth.description?.substring(0, 160) || `Visit ${booth.company_name}'s virtual booth on TradeHub24. View products, gallery, and connect with representatives.`}
        canonicalUrl={`${SEO_CONFIG.siteUrl}/booth/${boothId}`}
        ogImage={booth.banner_url || booth.logo_url || SEO_CONFIG.ogImage}
        ogType="business.business"
        structuredData={generateBoothStructuredData()}
      />
      
      {/* Banner Section */}
      <div className="relative">
        {/* Banner Image */}
        {booth.banner_url ? (
          <div className="h-48 md:h-64 bg-gradient-to-r from-primary to-primary/80 overflow-hidden">
            <OptimizedImage 
              src={booth.banner_url}
              alt={booth.company_name}
              className="w-full h-full opacity-90"
              objectFit="cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          </div>
        ) : (
          <div className="h-48 md:h-64 bg-gradient-to-r from-primary to-primary/80" />
        )}
        
        {/* Company Info Overlay - moved to bottom left of banner */}
        <div className="absolute bottom-4 left-0 right-0">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center space-x-4">
              {/* Logo - shown inline, not overlapping */}
              {booth.logo_url && (
                <div className="w-16 h-16 md:w-20 md:h-20 bg-white rounded-lg shadow-lg flex items-center justify-center overflow-hidden p-2 shrink-0">
                  <OptimizedImage 
                    src={booth.logo_url}
                    alt={booth.company_name}
                    className="max-w-full max-h-full"
                    objectFit="contain"
                  />
                </div>
              )}
              {/* Company Name and Slogan */}
              <div className="text-white">
                <h1 className="text-2xl md:text-3xl font-bold drop-shadow-lg" data-testid="booth-company-name">
                  {booth.company_name}
                </h1>
                {booth.slogan && (
                  <p className="text-sm md:text-base opacity-90 drop-shadow">
                    <AutoTranslatedText text={booth.slogan} translations={booth.slogan_translations} showLoading={false} />
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation and Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Back Buttons and Tier Badge */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Link to={event ? `/events/${event.id}` : '/exhibitors'}>
              <Button variant="ghost" size="sm">
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t('booth.back_to_exhibitors')}
              </Button>
            </Link>
            <Link to="/events">
              <Button variant="outline" size="sm">
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t('booth.all_events')}
              </Button>
            </Link>
          </div>
          {getTierBadge(booth.tier)}
        </div>

        {/* Tabs */}
        {accessChecked && !hasAccess ? (
          /* Access Restricted View */
          <div className="space-y-6">
            {/* Basic Info Card - Always Visible */}
            <Card>
              <CardHeader>
                <CardTitle>{t('booth.about')}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600" data-testid="booth-description">
                  <AutoTranslatedText text={booth.description || t('booth.no_description')} translations={booth.description_translations} showLoading={false} />
                </p>
              </CardContent>
            </Card>

            {/* Access Restricted Message */}
            <Card className="border-amber-200 bg-amber-50">
              <CardContent className="py-8">
                <div className="text-center">
                  <div className="mx-auto w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mb-4">
                    <Lock className="h-8 w-8 text-amber-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-amber-800 mb-2">
                    {t('booth.full_booth_restricted')}
                  </h3>
                  <p className="text-amber-700 mb-4 max-w-md mx-auto">
                    {!user 
                      ? t('booth.sign_in_to_view_booth')
                      : t('booth.register_to_view_booth')
                    }
                  </p>
                  <div className="flex justify-center gap-3">
                    {!user ? (
                      <Link to="/auth">
                        <Button>
                          <UserPlus className="h-4 w-4 mr-2" />
                          {t('booth.sign_in_register')}
                        </Button>
                      </Link>
                    ) : event && (
                      <Link to={`/events/${event.id}`}>
                        <Button>
                          <UserPlus className="h-4 w-4 mr-2" />
                          {t('booth.register_for_event')}
                        </Button>
                      </Link>
                    )}
                    <Link to="/events">
                      <Button variant="outline">
                        {t('booth.browse_events')}
                      </Button>
                    </Link>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Teaser - What's Hidden */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-slate-500 flex items-center gap-2">
                  <Lock className="h-4 w-4" />
                  {t('booth.restricted_content')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                  {galleryImages.length > 0 && (
                    <div className="p-4 bg-slate-100 rounded-lg">
                      <Image className="h-8 w-8 mx-auto text-slate-400 mb-2" />
                      <p className="text-sm text-slate-500">{t('booth.gallery_images_count', { count: galleryImages.length })}</p>
                    </div>
                  )}
                  {videos.length > 0 && (
                    <div className="p-4 bg-slate-100 rounded-lg">
                      <Video className="h-8 w-8 mx-auto text-slate-400 mb-2" />
                      <p className="text-sm text-slate-500">{t('booth.videos_count', { count: videos.length })}</p>
                    </div>
                  )}
                  {products.length > 0 && (
                    <div className="p-4 bg-slate-100 rounded-lg">
                      <Package className="h-8 w-8 mx-auto text-slate-400 mb-2" />
                      <p className="text-sm text-slate-500">{t('booth.products_count', { count: products.length })}</p>
                    </div>
                  )}
                  {pdfs.length > 0 && (
                    <div className="p-4 bg-slate-100 rounded-lg">
                      <FileText className="h-8 w-8 mx-auto text-slate-400 mb-2" />
                      <p className="text-sm text-slate-500">{t('booth.documents_count', { count: pdfs.length })}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        ) : (
        /* Full Access View */
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="overview">{t('booth.overview')}</TabsTrigger>
            <TabsTrigger value="gallery">{t('booth.gallery_videos')}</TabsTrigger>
            <TabsTrigger value="products">{t('booth.products')}</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Main Content */}
              <div className="lg:col-span-2 space-y-6">
                {/* About */}
                <Card>
                  <CardHeader>
                    <CardTitle>{t('booth.about')}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-600" data-testid="booth-description">
                      <AutoTranslatedText text={booth.description || t('booth.no_description')} translations={booth.description_translations} showLoading={false} />
                    </p>
                  </CardContent>
                </Card>

                {/* Gallery Preview */}
                {galleryImages.length > 0 && (
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between">
                      <CardTitle>{t('booth.gallery')}</CardTitle>
                      <Button variant="ghost" size="sm" onClick={() => setActiveTab('gallery')}>
                        {t('booth.view_all')}
                      </Button>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        {galleryImages.slice(0, 6).map((media) => (
                          <div key={media.url} className="group">
                            <div className="aspect-square bg-slate-100 rounded-lg overflow-hidden">
                              <img 
                                src={getImageUrl(media.url)}
                                alt={media.name || 'Gallery image'}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                {/* Connect Card - Only show interaction options when event is live */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">{t('booth.connect')}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {event?.status === 'live' ? (
                      <>
                        <Button 
                          className="w-full" 
                          data-testid="start-video-call-btn"
                          onClick={() => {
                            if (!user) {
                              toast.error(t('booth.sign_in_for_call'));
                              navigate('/auth');
                              return;
                            }
                            setInteractionType('call_request');
                            setInteractionMessage('');
                            setShowInteractionModal(true);
                          }}
                        >
                          <Video className="mr-2 h-4 w-4" />
                          {t('booth.request_call')}
                        </Button>
                        <Button 
                          variant="outline" 
                          className="w-full" 
                          data-testid="request-info-btn"
                          onClick={() => {
                            if (!user) {
                              toast.error(t('booth.sign_in_for_info'));
                              navigate('/auth');
                              return;
                            }
                            setInteractionType('info_request');
                            setInteractionMessage('');
                            setShowInteractionModal(true);
                          }}
                        >
                          <FileText className="mr-2 h-4 w-4" />
                          {t('booth.request_info')}
                        </Button>
                      </>
                    ) : (
                      <div className="text-center py-4">
                        <p className="text-sm text-slate-500">
                          {event?.status === 'registration_open' 
                            ? t('event_detail.video_messaging_live')
                            : t('booth.event_not_active')}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Contact Info */}
                {booth.contact_info && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">{t('booth.contact')}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 text-sm">
                      {booth.contact_info.email && (
                        <div className="flex items-center text-slate-600">
                          <Mail className="h-4 w-4 mr-2" />
                          {booth.contact_info.email}
                        </div>
                      )}
                      {booth.contact_info.phone && (
                        <div className="flex items-center text-slate-600">
                          <Phone className="h-4 w-4 mr-2" />
                          {booth.contact_info.phone}
                        </div>
                      )}
                      {booth.contact_info.website && (
                        <div className="flex items-center text-slate-600">
                          <Globe className="h-4 w-4 mr-2" />
                          <a href={booth.contact_info.website} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                            {booth.contact_info.website}
                          </a>
                        </div>
                      )}
                      {booth.contact_info.address && (
                        <div className="flex items-center text-slate-600">
                          <MapPin className="h-4 w-4 mr-2" />
                          {booth.contact_info.address}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Event Info */}
                {event && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">{t('booth.exhibiting_at')}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Link to={`/events/${event.id}`} className="block hover:bg-slate-50 -m-2 p-2 rounded">
                        <p className="font-medium text-slate-900">{event.name}</p>
                        <p className="text-sm text-slate-500">
                          {parseLocalDate(event.start_date).toLocaleDateString()} - {parseLocalDate(event.end_date).toLocaleDateString()}
                        </p>
                      </Link>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Gallery & Videos Tab */}
          <TabsContent value="gallery">
            <div className="space-y-8">
              {/* Gallery Images */}
              <div>
                <h3 className="text-xl font-semibold mb-4 flex items-center">
                  <Image className="h-5 w-5 mr-2" />
                  {t('booth.gallery')}
                </h3>
                {galleryImages.length === 0 ? (
                  <div className="bg-white border border-slate-200 rounded-lg p-12 text-center">
                    <Image className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">{t('booth.no_gallery_images')}</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {galleryImages.map((media) => (
                      <div key={media.id} className="aspect-square bg-slate-100 rounded-lg overflow-hidden">
                        <img 
                          src={getImageUrl(media.url)}
                          alt="Gallery"
                          className="w-full h-full object-cover hover:scale-105 transition-transform cursor-pointer"
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Videos */}
              <div>
                <h3 className="text-xl font-semibold mb-4 flex items-center">
                  <Play className="h-5 w-5 mr-2" />
                  {t('booth.videos')}
                </h3>
                {videos.length === 0 ? (
                  <div className="bg-white border border-slate-200 rounded-lg p-12 text-center">
                    <Video className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">{t('booth.no_videos')}</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {videos.map((media) => (
                      <div key={media.id} className="aspect-video bg-slate-900 rounded-lg overflow-hidden">
                        <video 
                          src={getImageUrl(media.url)}
                          className="w-full h-full object-contain"
                          controls
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* PDFs - Catalogs, Brochures & Publications */}
              <div>
                <h3 className="text-xl font-semibold mb-4 flex items-center">
                  <FileText className="h-5 w-5 mr-2" />
                  {t('booth.catalogs_brochures')}
                </h3>
                {pdfs.length === 0 ? (
                  <div className="bg-white border border-slate-200 rounded-lg p-12 text-center">
                    <FileText className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">{t('booth.no_documents')}</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {pdfs.map((media) => (
                      <a
                        key={media.id}
                        href={getImageUrl(media.url)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-white border border-slate-200 rounded-lg p-4 hover:shadow-lg transition-shadow flex items-start space-x-4 group"
                      >
                        {/* PDF Preview Icon */}
                        <div className="flex-shrink-0 w-16 h-20 bg-gradient-to-b from-red-50 to-red-100 border border-red-200 rounded-lg flex flex-col items-center justify-center">
                          <svg className="w-10 h-10 text-red-500" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20M10.92,12.31C10.68,11.54 10.15,9.08 11.55,9.04C12.95,9 12.03,12.16 12.03,12.16C12.42,13.65 14.05,14.72 14.05,14.72C14.55,14.57 17.4,14.24 17,15.72C16.57,17.2 13.5,15.81 13.5,15.81C11.55,15.95 10.09,16.47 10.09,16.47C8.96,18.58 7.64,19.5 7.1,18.61C6.43,17.5 9.23,16.07 9.23,16.07C10.68,13.72 10.9,12.35 10.92,12.31Z"/>
                          </svg>
                          <span className="text-[10px] font-bold text-red-600 mt-1">PDF</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-900 group-hover:text-primary transition-colors truncate">
                            {media.filename || 'Document.pdf'}
                          </p>
                          <p className="text-sm text-slate-500 mt-1">{t('booth.click_to_view')}</p>
                          <div className="flex items-center space-x-3 mt-2 text-xs text-slate-400">
                            <span className="flex items-center">
                              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                              {t('booth.view')}
                            </span>
                            <span className="flex items-center">
                              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                              </svg>
                              {t('booth.download')}
                            </span>
                            <span className="flex items-center">
                              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                              </svg>
                              {t('booth.print')}
                            </span>
                          </div>
                        </div>
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Products & Services Tab */}
          <TabsContent value="products">
            <div>
              <h3 className="text-xl font-semibold mb-4 flex items-center">
                <Package className="h-5 w-5 mr-2" />
                {t('booth.products')}
              </h3>
              {products.length === 0 ? (
                <div className="bg-white border border-slate-200 rounded-lg p-12 text-center">
                  <Package className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">{t('booth.no_products_available')}</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {products.map((product) => (
                    <Card key={product.id} className="overflow-hidden" data-testid={`product-card-${product.id}`}>
                      {/* Product Image */}
                      <div className="aspect-[3/4] bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center">
                        {product.image_url ? (
                          <img 
                            src={getImageUrl(product.image_url)}
                            alt={product.name}
                            className="max-w-full max-h-full object-contain"
                          />
                        ) : (
                          <Package className="h-12 w-12 text-slate-300" />
                        )}
                      </div>
                      <CardContent className="pt-4">
                        <h4 className="font-semibold text-slate-900 mb-1">{product.name}</h4>
                        <p className="text-sm text-slate-600 line-clamp-2 mb-2">{product.description}</p>
                        {product.price && (
                          <p className="text-lg font-bold text-primary">${product.price}</p>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
        )}
      </div>

      {/* AI Chat disabled - will reconnect when more shows/exhibitors available */}

      {/* Interaction Request Modal */}
      <Dialog open={showInteractionModal} onOpenChange={setShowInteractionModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {interactionType === 'call_request' ? t('booth.request_video_call_title') : t('booth.request_info_title')}
            </DialogTitle>
            <DialogDescription>
              {interactionType === 'call_request' 
                ? t('booth.request_call_desc', { company: booth?.company_name })
                : t('booth.request_info_desc', { company: booth?.company_name })
              }
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="message">{t('booth.message_optional')}</Label>
              <Textarea
                id="message"
                placeholder={interactionType === 'call_request' 
                  ? t('booth.call_placeholder')
                  : t('booth.info_placeholder')
                }
                value={interactionMessage}
                onChange={(e) => setInteractionMessage(e.target.value)}
                rows={4}
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowInteractionModal(false)}>
                {t('common.cancel')}
              </Button>
              <Button 
                onClick={async () => {
                  try {
                    await axios.post(`${API}/interactions`, {
                      booth_id: boothId,
                      type: interactionType,
                      message: interactionMessage || undefined
                    });
                    toast.success(
                      interactionType === 'call_request' 
                        ? t('booth.call_request_sent')
                        : t('booth.info_request_sent')
                    );
                    setShowInteractionModal(false);
                    setInteractionMessage('');
                  } catch (error) {
                    console.error('Failed to create interaction:', error);
                    toast.error(error.response?.data?.detail || t('booth.request_failed'));
                  }
                }}
                data-testid="submit-interaction-btn"
              >
                {interactionType === 'call_request' ? t('booth.request_call_btn') : t('booth.send_request')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BoothPage;
